import type { ReactNode } from "react";
import {
  getFieldChoiceRows,
  getChoicesBySourceField,
  getBirthdateAgeLimits,
  hasCityWhitelist,
  isDateWithTime,
  isFieldFilterable,
  isFieldInline,
  readChoicesSourceError,
} from "../../lib/forms/fieldOptions";
import {
  birthdateCalendarBounds,
  birthdateValidationMessage,
} from "../../lib/forms/dateField";
import { isPatronymicRequired } from "../../lib/forms/fio";
import { resolveFieldType } from "../../lib/forms/normalizeFieldType";
import type { FieldDto } from "../../types/field";
import type { ValuesByName } from "../../types/visibility";
import { getFieldValidation } from "../../lib/forms/getFieldValidation";
import { resolveNumberConfig } from "../../lib/forms/numberField";
import type { FormFieldRef } from "./types";
import { ChoiceInput } from "./inputs/choice-list";
import { ChoiceRadioTilesInput } from "./inputs/choice-list/radio-tiles";
import { CityInput } from "./inputs/city";
import { DateInput } from "./inputs/date";
import { DateRangeInput } from "./inputs/date-range";
import { EmailInput } from "./inputs/email";
import { PhoneInput } from "./inputs/phone";
import { TextInput } from "./inputs/text";
import { NumberInput } from "./inputs/number";
import { SelectInput } from "./inputs/select";
import { FioInput } from "./inputs/fio";
import { TextAreaInput } from "./inputs/text-area";
import { PrivacyInput } from "./inputs/privacy";
import { DisplayInput } from "./inputs/display";
import { MultipleChoiceInput } from "./inputs/multiple-choice";
import { FieldDescriptionProvider } from "./field-ui";
import { InlineFieldControl } from "./InlineFieldControl";
import { serializeFieldDescription } from "../../lib/forms/fieldDescription";
import { fieldAdminLabel } from "../../lib/forms/fieldLabel";
import { useRemoteFieldChoices } from "../../hooks/useRemoteFieldChoices";

interface FieldRendererProps {
  field: FieldDto;
  visible: boolean;
  inputRef: (instance: FormFieldRef) => void;
  onFieldValueChange: (fieldName: string, value: string) => void;
  valuesByName?: ValuesByName;
  layout?: "auto" | "stacked" | "inline";
  previewMode?: boolean;
  /** Скрыть label поля — вопрос рисует чат. */
  hideLabel?: boolean;
}

function resolveFieldLabel(field: FieldDto) {
  const trimmed = (field.label ?? "").trim();
  if (trimmed) return { text: trimmed, muted: false };
  return { text: "", muted: false };
}

function FieldGridCell({
  field,
  children,
}: {
  field: FieldDto;
  children: ReactNode;
}) {
  const isFullWidth =
    field.type === "textarea" ||
    resolveFieldType(field.type) === "privacy" ||
    resolveFieldType(field.type) === "display" ||
    resolveFieldType(field.type) === "date" ||
    resolveFieldType(field.type) === "date-range" ||
    resolveFieldType(field.type) === "birthdate" ||
    (field.label ?? "").trim().length > 38;
  const span = isFullWidth ? "sm:col-span-2" : "";
  return (
    <div className={`relative w-full min-w-0 ${span}`.trim()}>{children}</div>
  );
}

export function FieldRenderer({
  field,
  visible,
  inputRef,
  onFieldValueChange,
  valuesByName = {},
  layout = "auto",
  previewMode: _previewMode = false,
  hideLabel = false,
}: FieldRendererProps) {
  const remoteChoices = useRemoteFieldChoices(field, valuesByName);

  if (!visible) return null;

  const { text: labelText, muted: labelMuted } = hideLabel
    ? { text: "", muted: false }
    : resolveFieldLabel(field);
  const accessibleName = fieldAdminLabel(field);
  const fieldDescription = hideLabel
    ? ""
    : serializeFieldDescription(field.description) ?? "";

  const fieldType = resolveFieldType(field.type);
  const validation = getFieldValidation(field);
  const fieldId = String(field.id ?? "");
  const shared = {
    fieldId,
    fieldName: field.name,
    accessibleName,
    initialValue: valuesByName[field.name] ?? "",
    onValueChange: onFieldValueChange,
  };
  const staticRows = getFieldChoiceRows(field, valuesByName);
  const choiceRows = remoteChoices.active ? remoteChoices.rows : staticRows;
  const cityWhitelisted = hasCityWhitelist(field);
  const choicesBySource = getChoicesBySourceField(field);
  const sourceEmpty =
    Boolean(choicesBySource) &&
    (!(valuesByName[choicesBySource ?? ""] ?? "").trim() ||
      remoteChoices.sourceEmpty);
  const remoteError = remoteChoices.error || readChoicesSourceError(field);
  const emptyChoicesHint = sourceEmpty
    ? "Сначала выберите город"
    : remoteChoices.loading
      ? "Загрузка…"
      : remoteError && choiceRows.length === 0
        ? remoteError
        : undefined;
  const listDisabled = sourceEmpty || remoteChoices.loading;
  const useInlineLayout =
    !hideLabel &&
    (layout === "inline" || (layout === "auto" && isFieldInline(field)));

  if (useInlineLayout) {
    const inlineKind =
      fieldType === "email"
        ? "email"
        : fieldType === "phone"
          ? "phone"
          : fieldType === "birthdate"
            ? "birthdate"
            : fieldType === "date"
              ? "date"
              : fieldType === "city"
                ? "city"
                : fieldType === "choice" || fieldType === "select"
                  ? "choice"
                  : "text";
    const dateBounds =
      fieldType === "birthdate"
        ? birthdateCalendarBounds(getBirthdateAgeLimits(field))
        : {};

    return (
      <InlineFieldControl
        ref={inputRef}
        {...shared}
        label={labelText}
        labelMuted={labelMuted}
        fieldKind={inlineKind}
        withTime={fieldType === "date" && isDateWithTime(field)}
        min={dateBounds.min}
        max={dateBounds.max}
        choiceRows={
          fieldType === "choice" ||
          fieldType === "select" ||
          (fieldType === "city" && cityWhitelisted)
            ? choiceRows
            : undefined
        }
        disabled={fieldType === "select" ? listDisabled : false}
        emptyHint={fieldType === "select" ? emptyChoicesHint : undefined}
        validation={validation}
      />
    );
  }

  const labelProps = { placeholder: labelText, labelMuted };

  const wrap = (node: ReactNode) => (
    <FieldGridCell field={field}>
      <FieldDescriptionProvider description={fieldDescription}>
        {node}
      </FieldDescriptionProvider>
    </FieldGridCell>
  );

  switch (fieldType) {
    case "text":
      return wrap(
        <TextInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          validation={validation}
        />,
      );
    case "textarea":
      return wrap(
        <TextAreaInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          validation={validation}
        />,
      );
    case "email":
      return wrap(
        <EmailInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          validation={validation}
        />,
      );
    case "phone":
      return wrap(
        <PhoneInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          validation={validation}
        />,
      );
    case "number": {
      const numberConfig = resolveNumberConfig(field);
      return wrap(
        <NumberInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          required={field.required}
          min={numberConfig.min}
          max={numberConfig.max}
          step={numberConfig.step}
          integer={numberConfig.integer}
          validation={validation}
        />,
      );
    }
    case "city":
      return wrap(
        <CityInput
          ref={inputRef}
          {...shared}
          placeholder={labelText}
          labelMuted={labelMuted}
          validation={validation}
          allowedChoices={
            cityWhitelisted && choiceRows.length > 0 ? choiceRows : undefined
          }
        />,
      );
    case "choice":
      return wrap(
        <ChoiceRadioTilesInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          choiceRows={choiceRows}
          validation={validation}
        />,
      );
    case "select":
      if (isFieldFilterable(field)) {
        return wrap(
          <SelectInput
            ref={inputRef}
            {...shared}
            {...labelProps}
            choiceRows={choiceRows}
            disabled={listDisabled}
            emptyHint={emptyChoicesHint}
            validation={validation}
            inlineList={hideLabel}
          />,
        );
      }
      return wrap(
        <ChoiceInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          choiceRows={choiceRows}
          disabled={listDisabled}
          emptyHint={emptyChoicesHint}
          validation={validation}
          inlineList={hideLabel}
        />,
      );
    case "multiple-choice":
      return wrap(
        <MultipleChoiceInput
          ref={inputRef}
          {...shared}
          label={labelText}
          labelMuted={labelMuted}
          choiceRows={choiceRows}
          validation={validation}
        />,
      );
    case "fio":
      return wrap(
        <FioInput
          ref={inputRef}
          {...shared}
          label={labelText}
          labelMuted={labelMuted}
          required={field.required}
          patronymicRequired={isPatronymicRequired(field)}
        />,
      );
    case "date": {
      const withTime = isDateWithTime(field);
      return wrap(
        <DateInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          withTime={withTime}
          validation={validation}
          invalidMessage={() =>
            withTime ? "Укажите дату и время" : "Укажите корректную дату"
          }
        />,
      );
    }
    case "birthdate": {
      const limits = getBirthdateAgeLimits(field);
      const bounds = birthdateCalendarBounds(limits);
      return wrap(
        <DateInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          min={bounds.min}
          max={bounds.max}
          validation={validation}
          invalidMessage={(value) =>
            birthdateValidationMessage(value, limits) ??
            "Укажите корректную дату"
          }
        />,
      );
    }
    case "date-range":
      return wrap(
        <DateRangeInput
          ref={inputRef}
          {...shared}
          label={labelText}
          required={field.required}
          withTime={isDateWithTime(field)}
        />,
      );
    case "privacy":
      return wrap(
        <PrivacyInput ref={inputRef} {...shared} validation={validation} />,
      );
    case "display":
      return wrap(
        <DisplayInput
          ref={inputRef}
          {...shared}
          field={field}
          label={labelText}
          labelMuted={labelMuted}
        />,
      );
    default:
      if (import.meta.env.DEV && fieldType) {
        console.warn(
          `[chmforms] Неподдерживаемый тип поля "${field.type}" (${field.name}), показан как text`,
        );
      }
      return wrap(
        <TextInput
          ref={inputRef}
          {...shared}
          {...labelProps}
          validation={validation}
        />,
      );
  }
}
